/**
 * Responsibility: how tall a thing can be before it runs off the window.
 *
 * Replaces a guessed allowance. The old hook subtracted a constant 330px
 * for "top bar, metric cards, tabs row, padding" - which was right for the
 * one page it was written against and wrong everywhere else. Expanding the
 * collection hides the figures, so the table kept a cap 150px shorter than
 * the room it had and left a dead band under the last row.
 *
 * Measured instead: the element says where its own top is, and the height
 * left is whatever is below that. Nothing has to be told what is above it,
 * so nothing goes stale when the layout above changes.
 *
 * The measurement runs after every render rather than on a dependency
 * list. One `getBoundingClientRect` is cheap, the state only changes when
 * the number does, and a dependency list would be another thing to keep in
 * step with the layout - which is the mistake being fixed.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** Breathing room under the element, so it never touches the edge. */
const BOTTOM_GAP = 16;

/** Never shrink past this: a table with two visible rows is not a table. */
const MINIMUM = 280;

export function useFillHeight(): [(node: HTMLElement | null) => void, number] {
  const node = useRef<HTMLElement | null>(null);
  const [height, setHeight] = useState(MINIMUM);

  const measure = useCallback(() => {
    const element = node.current;
    if (element === null) return;
    const { top } = element.getBoundingClientRect();
    const next = Math.max(Math.round(window.innerHeight - top - BOTTOM_GAP), MINIMUM);
    // Only on a real change: setting the same number every render is a
    // render loop with extra steps.
    setHeight((current) => (current === next ? current : next));
  }, []);

  const ref = useCallback(
    (next: HTMLElement | null) => {
      node.current = next;
      measure();
    },
    [measure],
  );

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });

  return [ref, height];
}
